import { useState, useEffect, useCallback }           from '@wordpress/element';
import { __, sprintf, _n }                           from '@wordpress/i18n';
import { getWishlistIds, toggleWishlist, onWishlistChange } from '../../../utils/wishlist';
import { classNames }                                from '../../../utils/helpers';

export default function WishlistDrawer() {
	const [ open, setOpen ]         = useState( false );
	const [ ids, setIds ]           = useState( () => getWishlistIds() );
	const [ products, setProducts ] = useState( [] );
	const [ loading, setLoading ]   = useState( false );
	const [ error, setError ]       = useState( false );

	useEffect( () => {
		return onWishlistChange( ( newIds ) => setIds( [ ...newIds ] ) );
	}, [] );

	const fetchProducts = useCallback( async ( productIds ) => {
		if ( productIds.length === 0 ) {
			setProducts( [] );
			return;
		}
		setLoading( true );
		setError( false );
		try {
			const res = await fetch(
				`${ window.mwsbData.restUrl }/products?product_ids=${ productIds.join( ',' ) }&per_page=50`,
				{ headers: { 'X-WP-Nonce': window.mwsbData.nonce } }
			);
			if ( ! res.ok ) throw new Error();
			const data = await res.json();
			setProducts( data.products || [] );
		} catch {
			setError( true );
		} finally {
			setLoading( false );
		}
	}, [] );

	useEffect( () => {
		if ( open ) {
			fetchProducts( ids );
		}
	}, [ open, ids, fetchProducts ] );

	useEffect( () => {
		if ( ! open ) return;
		const handler = ( e ) => { if ( e.key === 'Escape' ) setOpen( false ); };
		document.addEventListener( 'keydown', handler );
		return () => document.removeEventListener( 'keydown', handler );
	}, [ open ] );

	useEffect( () => {
		document.body.style.overflow = open ? 'hidden' : '';
		return () => { document.body.style.overflow = ''; };
	}, [ open ] );

	useEffect( () => {
		const handler = () => setOpen( true );
		document.addEventListener( 'mwsb:open-wishlist', handler );
		return () => document.removeEventListener( 'mwsb:open-wishlist', handler );
	}, [] );

	const count = ids.length;

	const triggerLabel = count > 0
		? sprintf( _n( 'View wishlist, %d item', 'View wishlist, %d items', count, 'mwsb' ), count )
		: __( 'View wishlist', 'mwsb' );

	const drawerTitle = count > 0
		? sprintf( __( 'Wishlist (%d)', 'mwsb' ), count )
		: __( 'Wishlist', 'mwsb' );

	return (
		<>
			{ /* Floating trigger button */ }
			<button
				className="mwsb-wishlist-trigger"
				onClick={ () => setOpen( true ) }
				aria-label={ triggerLabel }
			>
				<HeartIcon filled={ count > 0 } />
				{ count > 0 && <span className="mwsb-wishlist-trigger__count">{ count }</span> }
			</button>

			{ /* Overlay */ }
			{ open && (
				<div
					className="mwsb-drawer-overlay"
					onClick={ () => setOpen( false ) }
					aria-hidden="true"
				/>
			) }

			{ /* Drawer panel */ }
			<div
				className={ `mwsb-wishlist-drawer${ open ? ' is-open' : '' }` }
				role="dialog"
				aria-label={ __( 'Wishlist', 'mwsb' ) }
				aria-modal="true"
			>
				<div className="mwsb-wishlist-drawer__header">
					<h2 className="mwsb-wishlist-drawer__title">
						{ drawerTitle }
					</h2>
					<button
						className="mwsb-wishlist-drawer__close"
						onClick={ () => setOpen( false ) }
						aria-label={ __( 'Close wishlist', 'mwsb' ) }
					>
						&#x2715;
					</button>
				</div>

				<div className="mwsb-wishlist-drawer__body">
					{ loading && (
						<div className="mwsb-wishlist-drawer__loading">
							<span className="mwsb-spinner mwsb-spinner--lg" />
						</div>
					) }

					{ ! loading && error && (
						<p className="mwsb-wishlist-drawer__empty">
							{ __( 'Could not load wishlist. Please try again.', 'mwsb' ) }
						</p>
					) }

					{ ! loading && ! error && count === 0 && (
						<p className="mwsb-wishlist-drawer__empty">
							{ __( 'Your wishlist is empty.', 'mwsb' ) }
						</p>
					) }

					{ ! loading && ! error && products.length > 0 && (
						<ul className="mwsb-wishlist-items">
							{ products.map( ( product ) => (
								<WishlistItem
									key={ product.id }
									product={ product }
									onRemove={ () => toggleWishlist( product.id ) }
								/>
							) ) }
						</ul>
					) }
				</div>
			</div>
		</>
	);
}

function WishlistItem( { product, onRemove } ) {
	const [ adding, setAdding ] = useState( false );
	const [ added, setAdded ]   = useState( false );
	const image      = product.images?.[ 0 ];
	const outOfStock = product.stock_status === 'outofstock';

	async function handleAddToCart() {
		if ( product.type !== 'simple' ) {
			window.location.href = product.permalink;
			return;
		}
		setAdding( true );
		try {
			const response = await fetch( window.mwsbData?.ajaxUrl, {
				method:  'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body:    new URLSearchParams( {
					action:     'woocommerce_add_to_cart',
					product_id: product.id,
					quantity:   1,
					security:   window.mwsbData?.nonce,
				} ),
			} );
			const data = await response.json();

			if ( window.jQuery && data.fragments ) {
				const $ = window.jQuery;
				$.each( data.fragments, function( selector, html ) {
					$( selector ).replaceWith( html );
				} );
				$( document.body ).trigger( 'added_to_cart', [ data.fragments, data.cart_hash ] );
			}

			window.wp?.data
				?.dispatch( 'wc/store/cart' )
				?.invalidateResolutionForStore?.();

			document.body.dispatchEvent(
				new CustomEvent( 'wc-blocks_added_to_cart', { bubbles: true, detail: { productId: product.id } } )
			);

			setAdded( true );
			setTimeout( () => setAdded( false ), 2000 );
		} finally {
			setAdding( false );
		}
	}

	return (
		<li className="mwsb-wishlist-item">
			{ image && (
				<a href={ product.permalink } className="mwsb-wishlist-item__image-link">
					<img
						src={ image }
						alt={ product.name }
						className="mwsb-wishlist-item__image"
						width="72"
						height="72"
						loading="lazy"
					/>
				</a>
			) }
			<div className="mwsb-wishlist-item__info">
				<a href={ product.permalink } className="mwsb-wishlist-item__name">
					{ product.name }
				</a>
				{ /* price_html comes from WooCommerce server — safe to inject. */ }
				<div
					className="mwsb-wishlist-item__price"
					dangerouslySetInnerHTML={ { __html: product.price_html } }
				/>
				<div className="mwsb-wishlist-item__actions">
					<button
						className={ classNames(
							'mwsb-btn mwsb-btn--cart mwsb-wishlist-item__cart-btn',
							adding && 'mwsb-btn--loading',
							outOfStock && 'mwsb-btn--disabled',
							added && 'mwsb-btn--added',
						) }
						onClick={ handleAddToCart }
						disabled={ outOfStock || adding }
					>
						{ adding && <span className="mwsb-spinner" /> }
						{ ! adding && added && __( 'Added', 'mwsb' ) }
						{ ! adding && ! added && ( outOfStock ? __( 'Out of Stock', 'mwsb' ) : ( product.add_to_cart?.text || __( 'Add to cart', 'mwsb' ) ) ) }
					</button>
					<button
						className="mwsb-wishlist-item__remove"
						onClick={ onRemove }
						aria-label={ sprintf( __( 'Remove %s from wishlist', 'mwsb' ), product.name ) }
					>
						{ __( 'Remove', 'mwsb' ) }
					</button>
				</div>
			</div>
		</li>
	);
}

function HeartIcon( { filled } ) {
	return (
		<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
			<path
				d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
				fill={ filled ? 'currentColor' : 'none' }
				stroke="currentColor"
				strokeWidth={ filled ? '0' : '2' }
			/>
		</svg>
	);
}
